/* eslint-disable no-console */

function getWeightsForSymptom(
  sym: string,
  weightsMap: Record<string, Record<string, number>>
): Record<string, number> {
  if (weightsMap[sym]) return weightsMap[sym];
  const cleaned = sym.trim().toLowerCase();
  for (const [k, v] of Object.entries(weightsMap)) {
    if (k.trim().toLowerCase() === cleaned) {
      return v;
    }
  }
  return {};
}

function getCountForSymptom(
  sym: string,
  countsMap: Record<string, number>,
  weightsMap: Record<string, Record<string, number>>
): number {
  if (countsMap[sym] !== undefined && countsMap[sym] > 0) {
    return countsMap[sym];
  }
  const cleaned = sym.trim().toLowerCase();
  for (const [k, v] of Object.entries(countsMap)) {
    if (k.trim().toLowerCase() === cleaned && v > 0) {
      return v;
    }
  }
  // Fallback si no está en countsMap: número de remedios con peso > 0
  const remWeights = getWeightsForSymptom(sym, weightsMap);
  const len = Object.keys(remWeights).length;
  return len > 0 ? len : 1;
}

function getHighRemediesCountsForSymptom(
  sym: string,
  countsHighMap: Record<string, { principal_high_remedies: number; total_high_remedies: number; ratio_high_remedies: number }> = {}
): { principal_high_remedies: number; total_high_remedies: number; ratio_high_remedies: number } {
  if (countsHighMap[sym]) return countsHighMap[sym];
  const cleaned = sym.trim().toLowerCase();
  for (const [k, v] of Object.entries(countsHighMap)) {
    if (k.trim().toLowerCase() === cleaned) {
      return v;
    }
  }
  return { principal_high_remedies: 0, total_high_remedies: 0, ratio_high_remedies: 0 };
}

export interface ReorderOptions {
  onlyPriority2?: boolean;
  symptomScores?: Record<string, number>;
  top5Remedies?: string[];
  polarDetails?: Record<string, any>;
  symptomRemedyHighRemediesCounts?: Record<string, { principal_high_remedies: number; total_high_remedies: number; ratio_high_remedies: number }>;
}

/**
 * Reordena una lista de preguntas (ítems pendientes) basándose en los pesos del grafo Neo4j.
 * Pondera el peso dividiendo por N = n_high_remedies + n_medium_remedies.
 *
 * @param pendingItems - Lista de ítems pendientes en bloques no visitados.
 * @param getSymptomRefs - Función que extrae las referencias a síntomas de un ítem (puede ser 1 o 2 por pregunta).
 * @param validatedSymptoms - Síntomas validados en pasos anteriores (vienen de la API).
 * @param currentSelectedSymptoms - Síntomas validados afirmativamente en el paso actual por el paciente.
 * @param symptomRemedyWeights - Mapa de { síntoma: { remedio: peso } } (viene de la API).
 * @param symptomRemedyCounts - Mapa de { síntoma: N } (donde N = n_high_remedies + n_medium_remedies).
 * @param linkedByRemedySymptoms - Lista de síntomas enlazados por relación LINKED_BY_REMEDY.
 * @param options - Opciones de configuración (ej. onlyPriority2 para el Paso 5 Intake).
 * @returns Lista de ítems reordenados en orden decreciente de peso normalizado (DESC).
 */
export function reorderQuestionsByWeight<T>(
  pendingItems: T[],
  getSymptomRefs: (item: T) => string[],
  validatedSymptoms: string[],
  currentSelectedSymptoms: string[],
  symptomRemedyWeights: Record<string, Record<string, number>>,
  symptomRemedyCounts: Record<string, number> = {},
  linkedByRemedySymptoms: string[] = [],
  options: ReorderOptions = {}
): T[] {
  if (!pendingItems.length) {
    return pendingItems;
  }

  const {
    onlyPriority2 = false,
    symptomScores = {},
    top5Remedies = [],
    polarDetails = {},
    symptomRemedyHighRemediesCounts = {}
  } = options;
  const linkedSet = new Set(linkedByRemedySymptoms.map((s) => s.trim().toLowerCase()));
  const allSelected = new Set([...validatedSymptoms, ...currentSelectedSymptoms]);

  // 1. Calcular el peso acumulado por remedio respecto a todos los síntomas validados
  const remedySelectedWeights: Record<string, number> = {};
  for (const selSym of allSelected) {
    const remWeights = getWeightsForSymptom(selSym, symptomRemedyWeights);
    for (const [remedy, w] of Object.entries(remWeights)) {
      if (w > 0) {
        remedySelectedWeights[remedy] = (remedySelectedWeights[remedy] || 0) + w;
      }
    }
  }

  // 2. Calcular el peso normalizado (W / N o SYMPTOMscore) e identificar priorización
  const itemsWithWeight = pendingItems.map((item, originalIndex) => {
    const refs = getSymptomRefs(item);
    let maxWeight = -Infinity;
    let maxRatioHigh = 0;
    let maxPrincipalHigh = 0;
    let maxRatioPN = 0;
    let isLinkedByRemedy = false;

    for (const ref of refs) {
      const cleanedRef = ref.trim().toLowerCase();
      if (
        linkedSet.has(cleanedRef) ||
        linkedSet.has(`mind_${cleanedRef}`) ||
        Array.from(linkedSet).some((ls) => ls.includes(cleanedRef) || cleanedRef.includes(ls))
      ) {
        isLinkedByRemedy = true;
      }

      const highInfo = getHighRemediesCountsForSymptom(ref, symptomRemedyHighRemediesCounts);
      if (highInfo.ratio_high_remedies > maxRatioHigh) {
        maxRatioHigh = highInfo.ratio_high_remedies;
      }
      if (highInfo.principal_high_remedies > maxPrincipalHigh) {
        maxPrincipalHigh = highInfo.principal_high_remedies;
      }

      const nVal = getCountForSymptom(ref, symptomRemedyCounts, symptomRemedyWeights);
      const ratioPN = nVal > 0 ? highInfo.principal_high_remedies / nVal : 0;
      if (ratioPN > maxRatioPN) {
        maxRatioPN = ratioPN;
      }

      let normalizedWeight = 0;
      if (symptomScores[ref] !== undefined || symptomScores[cleanedRef] !== undefined) {
        normalizedWeight = symptomScores[ref] ?? symptomScores[cleanedRef] ?? 0;
      } else {
        const remWeights = getWeightsForSymptom(ref, symptomRemedyWeights);
        let symWeight = 0;
        for (const [remedy, w] of Object.entries(remWeights)) {
          const sumWSel = remedySelectedWeights[remedy] || 0;
          if (sumWSel > 0 && w > 0) {
            symWeight += w * sumWSel;
          }
        }
        normalizedWeight = symWeight / nVal;
      }

      if (normalizedWeight > maxWeight) {
        maxWeight = normalizedWeight;
      }
    }

    if (maxWeight === -Infinity) maxWeight = 0;
    return {
      item,
      weight: maxWeight,
      ratioHighRemedies: maxRatioHigh,
      principalHighRemedies: maxPrincipalHigh,
      ratioPN: maxRatioPN,
      isLinkedByRemedy: onlyPriority2 ? false : isLinkedByRemedy,
      originalIndex
    };
  });

  // 3. Ordenar: si es Step 5 (onlyPriority2), omitir flag isLinkedByRemedy y ordenar solo por weight (DESC)
  itemsWithWeight.sort((a, b) => {
    if (!onlyPriority2 && a.isLinkedByRemedy !== b.isLinkedByRemedy) {
      return a.isLinkedByRemedy ? -1 : 1;
    }
    if (b.ratioHighRemedies !== a.ratioHighRemedies) {
      return b.ratioHighRemedies - a.ratioHighRemedies;
    }
    if (b.ratioPN !== a.ratioPN) {
      return b.ratioPN - a.ratioPN;
    }
    if (b.weight !== a.weight) {
      return b.weight - a.weight;
    }
    return a.originalIndex - b.originalIndex;
  });

  if (onlyPriority2) {
    console.group('🧪 [Step 5 Intake Reordering] Ordenación por Prioridad 2 (SYMPTOMscore / Ponderación Normalizada)');
    if (top5Remedies.length > 0) {
      console.log('🏆 Top 5 Remedios Predominantes (Inicio Paso 5):', top5Remedies);
    }
    console.log('✅ Síntomas validados hasta la fecha:', Array.from(allSelected));
    console.table(
      itemsWithWeight.map((x, idx) => {
        const refs = getSymptomRefs(x.item);
        const bestRemedies = refs.map((ref) => polarDetails[ref]?.best_remedy || 'N/A').join(' / ');
        const antagonists = refs.map((ref) => polarDetails[ref]?.antagonist || 'N/A').join(' / ');
        return {
          'New Rank': idx + 1,
          'Previous Rank': x.originalIndex + 1,
          'SYMPTOMscore (W_norm)': Number(x.weight.toFixed(4)),
          'Best Remedy': bestRemedies,
          'Antagonist': antagonists,
          'Symptom(s)': refs.join(' / '),
        };
      })
    );
    console.groupEnd();
  } else {
    console.group('⚖️ [Symptom Reordering] Ordenación y Pesos Normalizados');
    console.log('✅ Síntomas base (Pasos anteriores + Actual):', Array.from(allSelected));
    if (linkedByRemedySymptoms.length > 0) {
      console.log('🔗 Síntomas enlazados (LINKED_BY_REMEDY):', linkedByRemedySymptoms);
    }
    console.table(
      itemsWithWeight.map((x, idx) => {
        const refs = getSymptomRefs(x.item);
        const nVals = refs.map((ref) => getCountForSymptom(ref, symptomRemedyCounts, symptomRemedyWeights)).join(' / ');
        const highInfo = refs.map((ref) => getHighRemediesCountsForSymptom(ref, symptomRemedyHighRemediesCounts));
        const pHighStr = highInfo.map((f) => f.principal_high_remedies).join(' / ');
        const tHighStr = highInfo.map((f) => f.total_high_remedies).join(' / ');
        const rHighStr = highInfo.map((f) => f.ratio_high_remedies.toFixed(4)).join(' / ');
        const rPNStr = refs.map((ref) => {
          const nVal = getCountForSymptom(ref, symptomRemedyCounts, symptomRemedyWeights);
          const f = getHighRemediesCountsForSymptom(ref, symptomRemedyHighRemediesCounts);
          return (nVal > 0 ? f.principal_high_remedies / nVal : 0).toFixed(4);
        }).join(' / ');
        return {
          'New Rank': idx + 1,
          'Previous Rank': x.originalIndex + 1,
          'LINKED_BY_REMEDY': x.isLinkedByRemedy ? '⭐ YES (Prioritized)' : 'NO',
          'Ratio High Remedies (Principal/Total)': rHighStr,
          'Ratio P/N (Principal/N)': rPNStr,
          'Principal High Remedies': pHighStr,
          'Total High Remedies DB': tHighStr,
          'Normalized Weight (W/N)': Number(x.weight.toFixed(4)),
          'N Value': nVals,
          'Symptom(s)': refs.join(' / '),
        };
      })
    );
    console.groupEnd();
  }

  return itemsWithWeight.map((x) => x.item);
}

/**
 * Reordena únicamente las preguntas ubicadas en bloques aún no visitados por el paciente,
 * manteniendo intactos los índices y posiciones de las preguntas en bloques ya visitados.
 */
export function reorderUnvisitedQuestions<T>(
  questions: T[],
  blockSize: number,
  visitedBlocks: Set<number>,
  getSymptomRefs: (item: T) => string[],
  validatedSymptoms: string[],
  currentSelectedSymptoms: string[],
  symptomRemedyWeights: Record<string, Record<string, number>>,
  symptomRemedyCounts: Record<string, number> = {},
  linkedByRemedySymptoms: string[] = [],
  options: ReorderOptions = {}
): T[] {
  if (!questions.length) {
    return questions;
  }

  const unvisitedItems: T[] = [];
  const unvisitedIndices: number[] = [];

  questions.forEach((item, idx) => {
    const blockIdx = Math.floor(idx / blockSize);
    if (!visitedBlocks.has(blockIdx)) {
      unvisitedItems.push(item);
      unvisitedIndices.push(idx);
    }
  });

  if (!unvisitedItems.length) {
    return questions;
  }

  console.group('🔄 [Symptom Reordering] Actualizando Bloques No Visitados (Paso 5 Intake)');
  console.log('🔒 Bloques ya visitados (inmutables en pantalla):', Array.from(visitedBlocks));
  console.log(`📬 Reordenando ${unvisitedItems.length} preguntas pendientes en índices globales:`, unvisitedIndices);

  const reorderedUnvisited = reorderQuestionsByWeight(
    unvisitedItems,
    getSymptomRefs,
    validatedSymptoms,
    currentSelectedSymptoms,
    symptomRemedyWeights,
    symptomRemedyCounts,
    linkedByRemedySymptoms,
    options
  );

  const result = [...questions];
  unvisitedIndices.forEach((targetIdx, i) => {
    result[targetIdx] = reorderedUnvisited[i];
  });

  console.groupEnd();
  return result;
}
